import React from 'react';
import { motion } from 'framer-motion';
import './Stories.css';
import storiesData from '../../data/stories.json';

const WORKER_IMAGES = {
  sangeeta: '/stories/sangeeta.jpg',
  divya: '/stories/divya.jpg',
  sushma: '/stories/sushma.jpg',
  asma: '/stories/asma.jpg',
  uma: '/stories/uma.jpg',
  dharmender: '/stories/dharmender.jpg',
  fatima: '/stories/fatima.jpg',
};

const REPORT_URL =
  'https://drive.google.com/file/d/1ExF2jDtWGADK5cegL5OwTMwU-_fbLVaa/view';

const workerFindings = [
  { stat: '68.7%', text: 'of workers reported that heat affected their ability to work.' },
  { stat: '87.8%', text: 'of workers reported feeling completely drained at the end of the day during peak summer months.' },
  { stat: '87%', text: 'of workers reported facing heat-related issues such as headache, dizziness, weakness, and muscle cramps.' },
  { stat: '78%', text: 'of workers reported skipping breaks to meet production targets — and reported nearly double the stress scores of those who did not.' },
  { stat: '79.1%', text: 'of workers reported that break timings are fixed, making it difficult to take additional breaks even when feeling exhausted or overheated.' },
];

const factoryFindings = [
  { stat: '9 / 15', text: 'surveyed factories have no medical clinic or doctor. Of the 6 with an on-site clinic, 3 do not have a full-time doctor.' },
  { stat: '11 / 15', text: 'factories have roofs made of metal or asbestos.' },
  { stat: '7 / 15', text: 'factories have no devices to capture temperature or humidity. The remaining 8 that do take no measures to revise work schedules based on extreme heat.' },
  { stat: '4 / 7', text: 'factories with temperature devices only install them during buyer visits or inspections.' },
];

const LOGO_BASE = '/stories/Media Coverage Logos/';

const mediaOutlets = [
  {
    name: 'Sourcing Journal',
    logo: LOGO_BASE + 'Sourcing Journal .png',
    url: 'https://wwd.com/sourcing-journal/sustainability/india-garment-workers-factories-labor-extreme-heat-crisis-1238870719/',
  },
  {
    name: 'Business & Human Rights Resource Centre',
    logo: LOGO_BASE + 'Business and human Rights Centre.png',
    url: 'https://www.business-humanrights.org/en/latest-news/india-extreme-heat-on-garment-factory-floors-is-pushing-workers-to-breaking-point-study-finds/',
  },
  {
    name: 'The New Indian Express',
    logo: LOGO_BASE + 'The New Indian Express.webp',
    url: 'https://www.newindianexpress.com/nation/2026/Mar/02/extreme-heat-poses-crisis-for-indias-garment-workers-35-million-jobs-at-risk-by-2030-report',
  },
  {
    name: 'Down To Earth',
    logo: LOGO_BASE + 'Down to Earth.jpg',
    url: 'https://www.downtoearth.org.in/climate-change/working-in-a-furnace-heat-is-making-indias-garment-workers-sick',
  },
  {
    name: 'India Today',
    logo: LOGO_BASE + 'India Today.png',
    url: 'https://www.indiatoday.in/india-today-insight/story/did-noida-workers-protest-in-the-heat-of-the-moment-climate-change-study-says-2899999-2026-04-22',
  },
  {
    name: 'EcoTextile',
    logo: LOGO_BASE + 'Ecotextile.png',
    url: 'https://www.ecotextile.com/2026030361901/news/social-compliance-and-csr/heat-stress-pushes-garment-workers-to-breaking-point/',
  },
  {
    name: 'Newslaundry',
    logo: LOGO_BASE + 'Newslaundry.jpg',
    url: 'https://www.newslaundry.com/2026/04/13/as-india-braces-for-summer-informal-workers-have-little-heat-protection',
  },
];

const sectionAnim = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.6, ease: 'easeOut' },
};

function highlightUrgentText(text) {
  if (!text) return text;
  const terms = [
    'heat', 'oven', 'boil', 'faint', 'tin roof', 'exhausted', 'machine', 'fired',
    'abuse', 'crumbs', 'burning', 'boils', 'trapped', 'production pressure', 'exhaustion'
  ];
  const pattern = new RegExp(`\\b(${terms.join('|')})\\w*\\b`, 'gi');

  const parts = text.split(pattern);
  if (parts.length === 1) return text;

  return parts.map((part, i) => {
    if (i % 2 === 1) {
      return <span key={i} className="s-highlight-orange">{part}</span>;
    }
    return part;
  });
}

function SectionHeader({ label, heading }) {
  return (
    <div className="s-section-head">
      <span className="s-section-label">{label}</span>
      <div className="s-section-rule" />
      {heading && <h2 className="s-section-heading">{heading}</h2>}
    </div>
  );
}

function FindingsCardGrid({ items, variant }) {
  const gridClass = `s-findings-card-grid s-findings-grid--${variant}`;
  return (
    <ul className={gridClass}>
      {items.map((item, i) => {
        const isWide = variant === 'worker' && i === items.length - 1 && items.length % 2 === 1;
        return (
          <li
            key={i}
            className={`s-finding-card s-finding-card--${variant}${isWide ? ' s-finding-card--wide' : ''}`}
          >
            <strong className="s-finding-stat">{item.stat}</strong>
            <hr className="s-finding-rule" />
            <span className="s-finding-text">{highlightUrgentText(item.text)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Stories() {
  return (
    <div className="stories-container">
      <div className="stories-paper">

        {/* ── Page header ───────────────────────── */}
        <motion.header
          className="stories-header"
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <h1 className="stories-headline">From the factory floor</h1>
          <p className="stories-lede">
            These are accounts from garment and textile workers across India,
            collected as part of the Heatwatch research. They describe conditions
            on the production lines where global fashion is made.
          </p>
        </motion.header>



        {/* ── About the Report ──────────────────── */}
        <motion.section className="s-section" {...sectionAnim}>
          <SectionHeader label="About the Report" heading="Breaking Point: Heat and the Garment Floor" />
          <div className="s-two-col">
            <div className="s-about-left-col">
              <img
                src="/stories/report-cover.png"
                alt="Breaking Point: Heat and the Garment Floor — report cover"
                className="s-report-cover"
              />
            </div>
            <div>
              <p className="s-body">
                As temperatures rise across the globe, heat stress is reshaping working
                conditions inside garment factories. In response, HeatWatch in
                collaboration with the Tata Institute of Social Sciences (TISS) published{' '}
                <a href={REPORT_URL} target="_blank" rel="noopener noreferrer" className="s-link">
                  <em>Breaking Point: Heat and the Garment Floor</em>
                </a>
                , examining how rising heat is affecting worker health, productivity, and
                working conditions across India's garment supply chains.
              </p>
              <p className="s-body s-mt">
                Drawing from worker surveys, in-depth interviews, and factory case studies
                across Tamil Nadu, Delhi-NCR, and Gujarat, the report combines worker
                testimonies with on-ground factory observations to better understand how
                climate change is intersecting with labour conditions within global garment
                production networks. The report seeks to contribute to wider conversations
                around labour rights, climate adaptation, ethical sourcing, occupational
                safety, and business responsibility within the garment sector.
              </p>
            </div>
          </div>
        </motion.section>

        <div className="s-divider" />

        {/* ── Key Findings ──────────────────────── */}
        <motion.section className="s-section" {...sectionAnim}>
          <SectionHeader label="Key Findings" heading="What the data shows" />
          <p className="s-body s-findings-intro">
            Drawing from surveys of <strong>115 garment workers</strong> in Tamil Nadu
            and Delhi-NCR, <strong>47 in-depth interviews</strong>, and factory case
            studies across <strong>15 garment and textile units</strong> in Tamil Nadu,
            Delhi-NCR, and Gujarat, the report offers a first-hand look at how rising
            heat is affecting worker well-being and compromising the sector's competence.
          </p>
          <div className="s-findings-block">
            <h3 className="s-findings-title">Worker Experiences &amp; Health Impacts</h3>
            <FindingsCardGrid items={workerFindings} variant="worker" />
          </div>

          <div className="s-findings-block">
            <h3 className="s-findings-title">Factory Infrastructure &amp; Preparedness</h3>
            <p className="s-findings-factory-note">
              Across 15 factories in Tiruppur, Faridabad, Gurugram, Noida &amp; Surat
            </p>
            <FindingsCardGrid items={factoryFindings} variant="factory" />
          </div>
        </motion.section>

        <div className="s-divider" />

        {/* ── Why These Findings Matter ─────────── */}
        <motion.section className="s-section" {...sectionAnim}>
          <SectionHeader label="Why These Findings Matter" heading="Heat is invisible in supply chain monitoring" />
          <div className="s-two-col">
            <p className="s-body">
              The findings highlight how rising heat is changing working conditions across
              India's garment sector, affecting worker health, productivity, and everyday
              factory operations. Yet despite the scale of the issue, significant gaps
              remain in publicly accessible data linking heat conditions to garment
              production locations and worker populations.
            </p>
            <p className="s-body">
              Worker testimonies and factory-level observations reveal that heat exposure
              is widespread, yet often remains invisible within conventional supply chain
              monitoring systems. The absence of temperature monitoring in many
              factories — alongside the lack of operational changes even where temperatures
              are recorded — points to a significant gap in how climate risks are currently
              understood and managed within garment production networks.
            </p>
          </div>
        </motion.section>

        <div className="s-divider" />

        {/* ── In the Media ──────────────────────── */}
        <motion.section className="s-section" {...sectionAnim}>
          <SectionHeader label="In the Media" heading="Coverage and reach" />
          <div className="s-media-layout">
            <p className="s-body">
              The report and its findings have received coverage across national and
              international media, drawing attention to the growing impact of extreme heat
              inside garment factories and questions of labour rights, workplace safety,
              climate adaptation, and supply chain responsibility. Coverage by{' '}
              <em>Sourcing Journal</em> and the{' '}
              <em>Business &amp; Human Rights Resource Centre</em> highlighted the need
              for stronger supplier protections, workplace heat mitigation measures, and
              greater corporate accountability within global garment supply chains. The
              report has also helped start conversations on garment worker conditions in
              India through coverage in <em>The New Indian Express</em>,{' '}
              <em>Down To Earth</em>, <em>India Today</em>, <em>Carbon Copy</em>,{' '}
              <em>EcoTextile</em>, and <em>Newslaundry</em>.
            </p>
            <div className="s-media-coverage-block">
              <p className="s-coverage-label">Coverage in:</p>
              <div className="s-media-strip">
                {mediaOutlets.map((outlet) => (
                  <a
                    key={outlet.name}
                    href={outlet.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="s-media-logo-link"
                    title={outlet.name}
                  >
                    {outlet.logo ? (
                      <img
                        src={outlet.logo}
                        alt={outlet.name}
                        className="s-media-logo-img"
                      />
                    ) : (
                      <span className="s-media-text-pill">{outlet.name}</span>
                    )}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        <div className="s-divider" />

        {/* ── Worker Stories ────────────────────── */}
        <section className="s-section s-worker-section">
          <SectionHeader label="Worker Stories" heading="In their own words" />
          <p className="s-anon-note">Names of the workers have been anonymized.</p>

          <div className="s-worker-list">
            {storiesData.map((story, index) => {
              const metaParts = [
                story.name,
                story.location,
                story.age ? `Age ${story.age}` : null,
              ].filter(Boolean);

              const isReversed = index % 2 === 1;

              return (
                <motion.article
                  key={story.id}
                  className={`s-worker-row${isReversed ? ' s-worker-row--reversed' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                  <div className="s-worker-col-image">
                    <img
                      src={WORKER_IMAGES[story.name.toLowerCase()] || ''}
                      alt={story.name}
                      className="s-worker-image"
                    />
                  </div>
                  <div className="s-worker-col-text">
                    <p className="s-worker-meta">{metaParts.join(' · ')}</p>
                    <p className="s-worker-quote">{highlightUrgentText(story.quote)}</p>
                    <p className="s-worker-bio">{highlightUrgentText(story.story)}</p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </section>

        {/* ── Footer ───────────────────────────── */}
        <footer className="s-footer">
          <p>
            Accounts collected as part of{' '}
            <a href={REPORT_URL} target="_blank" rel="noopener noreferrer" className="s-link">
              <em>Breaking Point: Heat and the Garment Floor</em>
            </a>{' '}
            — a report by HeatWatch in collaboration with the Tata Institute of Social
            Sciences (TISS).
          </p>
        </footer>

      </div>
    </div>
  );
}

export default Stories;
